// Pure consistency checks for the invoice workflow editor.
//
// No network, no SQL, no business arithmetic beyond surfacing net+VAT/gross
// mismatches the operator must resolve before approval. Unknown (null/undefined)
// is never treated as zero: a missing total simply skips that comparison and is
// reported as missing instead. Drafts may stay incomplete; approval requires
// the full set below, mirroring backend invoiceImportValidation bounds
// (positive quantities/FX, pcs/m2/m3 units, 0-100 VAT, non-negative money).
import { ORDER_CURRENCIES } from '../orders/currencies';
import { isWarehouseQuantity } from '../../utils/warehouseQuantity';
import {
  isStockGeneratingKind,
  type WorkflowInvoice,
  type WorkflowInvoiceLine,
} from '../../types/workflowInvoice';

export interface WorkflowInvoiceIssue {
  field: string;
  key: string;
}

const TOLERANCE = 0.01;

function isKnown(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isPositive(value: unknown): value is number {
  return isKnown(value) && value > 0;
}

function isNonNegative(value: unknown): value is number {
  return isKnown(value) && value >= 0;
}

function amountsMatch(a: number, b: number): boolean {
  return Math.abs(a - b) <= TOLERANCE;
}

export function lineTotalsConsistent(line: WorkflowInvoiceLine): boolean {
  if (!isKnown(line.netAmount) || !isKnown(line.vatAmount) || !isKnown(line.grossAmount)) {
    return true;
  }
  return amountsMatch(line.netAmount + line.vatAmount, line.grossAmount);
}

export function documentTotalsConsistent(invoice: WorkflowInvoice): boolean {
  if (!invoice.linesLoaded || invoice.lines.length === 0) return true;
  const sums = invoice.lines.reduce(
    (acc, line) => ({
      net: acc.net + (isKnown(line.netAmount) ? line.netAmount : NaN),
      vat: acc.vat + (isKnown(line.vatAmount) ? line.vatAmount : NaN),
      gross: acc.gross + (isKnown(line.grossAmount) ? line.grossAmount : NaN),
    }),
    { net: 0, vat: 0, gross: 0 },
  );
  if (!isKnown(invoice.totalNet) || !isKnown(invoice.totalVat) || !isKnown(invoice.totalGross)) {
    return true;
  }
  if (Number.isNaN(sums.net) || Number.isNaN(sums.vat) || Number.isNaN(sums.gross)) {
    return true;
  }
  return (
    amountsMatch(sums.net, invoice.totalNet) &&
    amountsMatch(sums.vat, invoice.totalVat) &&
    amountsMatch(sums.gross, invoice.totalGross) &&
    amountsMatch(invoice.totalNet + invoice.totalVat, invoice.totalGross)
  );
}

function isSupportedCurrency(currency: string | null | undefined): boolean {
  return typeof currency === 'string' &&
    (ORDER_CURRENCIES as readonly string[]).includes(currency);
}

/** Issues blocking approval; drafts may stay incomplete but must show them. */
export function validateWorkflowInvoiceForApproval(invoice: WorkflowInvoice): WorkflowInvoiceIssue[] {
  const issues: WorkflowInvoiceIssue[] = [];
  if (invoice.kind !== 'STANDARD') issues.push({ field: 'kind', key: 'invoices:workflow.validation.standardRequired' });

  if (!invoice.originalDocumentNumber) {
    issues.push({ field: 'originalDocumentNumber', key: 'invoices:workflow.validation.originalNumberRequired' });
  }
  if (!invoice.sellerTaxId && !invoice.sellerName) {
    issues.push({ field: 'seller', key: 'invoices:workflow.validation.sellerRequired' });
  }
  if (!invoice.issueDate) {
    issues.push({ field: 'issueDate', key: 'invoices:workflow.validation.issueDateRequired' });
  }
  if (!invoice.currency) {
    issues.push({ field: 'currency', key: 'invoices:workflow.validation.currencyRequired' });
  } else if (!isSupportedCurrency(invoice.currency)) {
    issues.push({ field: 'currency', key: 'invoices:workflow.validation.currencyUnsupported' });
  } else if (invoice.currency !== 'PLN' && !isPositive(invoice.exchangeRateToPLN)) {
    issues.push({ field: 'exchangeRateToPLN', key: 'invoices:workflow.validation.exchangeRateRequired' });
  }
  if (!isNonNegative(invoice.totalNet)) {
    issues.push({ field: 'totalNet', key: 'invoices:workflow.validation.totalNetRequired' });
  }
  if (!isNonNegative(invoice.totalVat)) {
    issues.push({ field: 'totalVat', key: 'invoices:workflow.validation.totalVatRequired' });
  }
  if (!isNonNegative(invoice.totalGross)) {
    issues.push({ field: 'totalGross', key: 'invoices:workflow.validation.totalGrossRequired' });
  }
  if (!invoice.linesLoaded || invoice.lines.length === 0) {
    issues.push({ field: 'lines', key: 'invoices:workflow.validation.linesRequired' });
    return issues;
  }

  invoice.lines.forEach((line, index) => {
    const prefix = `lines[${index}]`;
    if (!line.productName) {
      issues.push({ field: `${prefix}.productName`, key: 'invoices:workflow.validation.lineProductRequired' });
    }
    if (!isKnown(line.quantity) || !isWarehouseQuantity(line.quantity)) {
      issues.push({ field: `${prefix}.quantity`, key: 'invoices:workflow.validation.lineQuantityRequired' });
    }
    if (line.unit !== 'pcs' && line.unit !== 'm2' && line.unit !== 'm3') {
      issues.push({ field: `${prefix}.unit`, key: 'invoices:workflow.validation.lineUnitRequired' });
    }
    if (!isPositive(line.commercialQuantity) || !line.commercialUnit) {
      issues.push({ field: `${prefix}.commercial`, key: 'invoices:workflow.validation.lineCommercialRequired' });
    }
    if (!isNonNegative(line.unitPrice)) {
      issues.push({ field: `${prefix}.unitPrice`, key: 'invoices:workflow.validation.linePriceRequired' });
    }
    if (!isNonNegative(line.netAmount) || !isNonNegative(line.vatAmount) || !isNonNegative(line.grossAmount)) {
      issues.push({ field: `${prefix}.amounts`, key: 'invoices:workflow.validation.lineAmountsRequired' });
    } else if (!lineTotalsConsistent(line)) {
      issues.push({ field: `${prefix}.amounts`, key: 'invoices:workflow.validation.lineAmountsInconsistent' });
    }
    if (isKnown(line.taxRate) && (line.taxRate < 0 || line.taxRate > 100)) {
      issues.push({ field: `${prefix}.taxRate`, key: 'invoices:workflow.validation.lineVatRateInvalid' });
    }
    if (!line.sourceOrderLineId || !line.matchConfirmed) {
      issues.push({ field: `${prefix}.match`, key: 'invoices:workflow.validation.lineMatchRequired' });
    }
  });

  if (!documentTotalsConsistent(invoice)) {
    issues.push({ field: 'totals', key: 'invoices:workflow.validation.totalsInconsistent' });
  }

  return issues;
}

/** Non-standard kinds never generate stock, even when fully matched. */
export function canGenerateStock(invoice: Pick<WorkflowInvoice, 'kind'>): boolean {
  return isStockGeneratingKind(invoice.kind);
}
